import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Users, ChevronLeft, ChevronRight, User, Play, Volume2, VolumeX, X } from 'lucide-react';
import axios from 'axios';
import AppShell from '../components/AppShell';
import ReportButton from '../components/ReportButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve a stored intro video URL (may be relative `/api/uploads/...`) to a full URL.
const resolveVideoUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

// Detect touch/coarse-pointer devices so we skip hover-preview and use tap-to-modal instead.
const isCoarsePointer = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(pointer: coarse)').matches;

function IntroModal({ member, onClose }) {
  const videoRef = useRef(null);
  useEffect(() => {
    // Auto-play when opened.
    const v = videoRef.current;
    if (v) {
      v.muted = false;
      v.play().catch(() => {});
    }
  }, []);
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="intro-modal"
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl overflow-hidden bg-brand-surface border border-brand-gold/40"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white hover:bg-black/80"
          data-testid="intro-modal-close"
          aria-label="Close intro video"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="absolute top-3 right-14 z-10">
          <ReportButton videoType="intro" videoId={member.id} />
        </div>
        <video
          ref={videoRef}
          src={resolveVideoUrl(member.microsite_intro_video_url)}
          controls
          playsInline
          className="w-full aspect-video bg-black"
          data-testid="intro-modal-video"
        />
        <div className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <p className="font-semibold text-white">{member.display_name || member.name}</p>
            <span className="px-1.5 py-0.5 rounded bg-brand-gold/20 text-brand-gold text-[10px] font-bold">
              {(member.membership_tier || 'ascend').toUpperCase()}
            </span>
          </div>
          <p className="text-xs text-gray-400 mb-3">
            {member.specialty || 'Member'}{member.city ? ` · ${member.city}` : ''}
          </p>
          <Link
            to={`/member/${member.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-4 py-2 bg-brand-gold text-black text-xs font-semibold rounded-lg hover:brightness-110"
            data-testid="intro-modal-full-profile-btn"
          >
            Full profile →
          </Link>
        </div>
      </div>
    </div>
  );
}

function MemberCard({ member, onOpenIntro }) {
  const isFounderTier = member.membership_tier === 'founder';
  const isGrowthTier = member.membership_tier === 'growth';
  const ringClass = isFounderTier ? 'ring-brand-gold/40' : isGrowthTier ? 'ring-brand-blue/30' : 'ring-slate-500/30';
  const hoverBorder = isFounderTier ? 'hover:border-brand-gold/30' : isGrowthTier ? 'hover:border-brand-blue/30' : 'hover:border-slate-400/30';
  const tierBadge = isFounderTier ? 'bg-brand-gold/20 text-brand-gold' : isGrowthTier ? 'bg-brand-blue/20 text-blue-300' : 'bg-slate-500/25 text-slate-300';

  const hasIntro = !!(member.has_intro || member.microsite_intro_video_url);
  const introUrl = resolveVideoUrl(member.microsite_intro_video_url);

  const [isPlaying, setIsPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0); // 0..1
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const videoRef = useRef(null);
  const hoverTimer = useRef(null);

  const fmt = (s) => {
    if (!Number.isFinite(s) || s < 0) return '0:00';
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${m}:${r.toString().padStart(2, '0')}`;
  };

  const startHover = () => {
    if (!hasIntro || isCoarsePointer()) return;
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setIsPlaying(true), 400);
  };

  const endHover = () => {
    clearTimeout(hoverTimer.current);
    setIsPlaying(false);
    setMuted(true);
    setProgress(0);
    setCurrent(0);
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isPlaying) {
      v.currentTime = 0;
      v.muted = muted;
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    const v = videoRef.current;
    if (v) v.muted = muted;
  }, [muted]);

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setCurrent(v.currentTime);
    setDuration(v.duration);
    setProgress(v.currentTime / v.duration);
  };

  const handleBadgeClick = (e) => {
    // On touch devices open the modal; on desktop the hover already handles playback.
    if (isCoarsePointer()) {
      e.preventDefault();
      e.stopPropagation();
      onOpenIntro(member);
    }
  };

  const playingBorder = isPlaying
    ? 'border-brand-gold/60 shadow-[0_0_30px_rgba(212,162,74,0.18)]'
    : `border-white/5 ${hoverBorder}`;

  return (
    <div
      className={`rounded-2xl bg-brand-surface border transition-all group overflow-hidden ${playingBorder}`}
      onMouseEnter={startHover}
      onMouseLeave={endHover}
      data-testid="member-card"
    >
      {isPlaying && hasIntro ? (
        // Video preview replaces the top of the card.
        <div className="relative aspect-video bg-black">
          <video
            ref={videoRef}
            src={introUrl}
            playsInline
            muted={muted}
            onTimeUpdate={onTimeUpdate}
            onEnded={endHover}
            className="w-full h-full object-cover"
            data-testid="intro-hover-video"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
          <div className="absolute top-3 left-3 flex items-center gap-2 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur">
            <div className="flex items-end gap-[2px] h-3">
              <span className="eq-bar" />
              <span className="eq-bar" />
              <span className="eq-bar" />
              <span className="eq-bar" />
            </div>
            <span className="text-[10px] font-semibold text-brand-gold uppercase tracking-wider">
              Meet {(member.display_name || member.name || 'them').split(' ')[0]}
            </span>
          </div>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMuted((m) => !m); }}
            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white hover:bg-black/80"
            data-testid={`intro-mute-toggle-${member.id}`}
            aria-label={muted ? 'Unmute intro' : 'Mute intro'}
          >
            {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
          </button>
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/10">
            <div className="h-full bg-brand-gold" style={{ width: `${Math.min(100, progress * 100)}%` }} />
          </div>
          <div className="absolute bottom-2 left-3 text-[10px] text-gray-300 font-mono">
            {fmt(current)} / {fmt(duration)}
          </div>
        </div>
      ) : (
        <div className="p-5 pb-0">
          <Link to={`/member/${member.id}`} target="_blank" rel="noopener noreferrer" className="block">
            <div className="flex items-start gap-3 mb-3">
              <div className="relative flex-shrink-0">
                {member.profile_photo ? (
                  <img src={member.profile_photo} alt={member.name} className={`w-14 h-14 rounded-full object-cover ring-2 ${ringClass}`} />
                ) : (
                  <div className={`w-14 h-14 rounded-full bg-white/5 flex items-center justify-center ring-2 ${ringClass}`}>
                    <User className="w-6 h-6 text-gray-500" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-white truncate">{member.display_name || member.name}</p>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${tierBadge}`}>
                    {(member.membership_tier || 'ascend').toUpperCase()}
                  </span>
                </div>
                <p className="text-xs text-gray-400 truncate">
                  {member.specialty || 'Member'}{member.city ? ` · ${member.city}` : ''}
                </p>
              </div>
              {hasIntro && (
                <button
                  type="button"
                  onClick={handleBadgeClick}
                  className="intro-pulse flex items-center gap-1 px-2 py-1 rounded-full bg-brand-gold text-black text-[10px] font-bold cursor-pointer flex-shrink-0"
                  data-testid={`intro-badge-${member.id}`}
                  aria-label="Play member intro video"
                  title="Hover to preview · tap to play"
                >
                  <Play className="w-2.5 h-2.5 fill-current" /> INTRO
                </button>
              )}
            </div>
            {member.bio && (
              <p className="text-xs text-gray-300 mb-3 line-clamp-2">{member.bio}</p>
            )}
            <div className="flex items-center gap-2 mb-4 text-xs">
              <span className="text-brand-gold">★ {(member.rating || 5.0).toFixed(1)}</span>
              <span className="text-gray-500">({member.review_count || 0} reviews)</span>
            </div>
          </Link>
        </div>
      )}
      <div className={`flex gap-2 ${isPlaying ? 'p-4' : 'px-5 pb-5'}`}>
        <Link to={`/member/${member.id}`} target="_blank" rel="noopener noreferrer" className="flex-1">
          <button className="w-full px-3 py-2 bg-brand-gold text-black text-xs font-semibold rounded-lg hover:brightness-110" data-testid={`book-${member.id}`}>
            {isPlaying ? 'Full profile →' : 'View Profile'}
          </button>
        </Link>
      </div>
    </div>
  );
}

const tierConfig = {
  founder: { label: 'Founder', color: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/30' },
  growth: { label: 'Growth', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
  ascend: { label: 'Ascend', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
};

export default function MemberDirectory() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [introOnly, setIntroOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [introMember, setIntroMember] = useState(null); // for mobile tap-to-play modal

  useEffect(() => { fetchMembers(); }, [page, tierFilter, introOnly]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 12 });
      if (tierFilter !== 'all') params.append('tier', tierFilter);
      if (introOnly) params.append('has_intro', '1');
      if (search.trim()) params.append('search', search.trim());
      const res = await axios.get(`${API_URL}/api/directory/members?${params}`);
      setMembers(res.data.members || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to load directory:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchMembers();
  };

  return (
    <AppShell active="directory" title="Community">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="directory-root">
        <div className="mb-8">
          <h1 className="font-playfair text-4xl text-white" data-testid="directory-title">Member Directory</h1>
          <p className="text-gray-400 mt-1 text-sm">Discover {total.toLocaleString()} members across the globe. Book services, watch videos, and connect.</p>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap gap-2 mb-6 text-sm items-center">
          {[
            { id: 'all', label: `All (${total.toLocaleString()})`, classes: tierFilter === 'all' ? 'bg-white/10 text-white' : 'text-gray-400 hover:bg-white/5' },
            { id: 'founder', label: 'Founder', classes: tierFilter === 'founder' ? 'bg-brand-gold/15 text-brand-gold border border-brand-gold/40' : 'text-brand-gold border border-brand-gold/30 hover:bg-brand-gold/10' },
            { id: 'growth', label: 'Growth', classes: tierFilter === 'growth' ? 'bg-brand-blue/20 text-blue-200 border border-brand-blue/40' : 'text-blue-300 border border-brand-blue/30 hover:bg-brand-blue/10' },
            { id: 'ascend', label: 'Ascend', classes: tierFilter === 'ascend' ? 'bg-slate-500/20 text-slate-200 border border-slate-500/40' : 'text-slate-300 border border-slate-500/30 hover:bg-slate-500/10' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => { setTierFilter(f.id); setPage(1); }}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${f.classes}`}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}

          {/* "With intro" filter — pulses when active */}
          <button
            onClick={() => { setIntroOnly((v) => !v); setPage(1); }}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              introOnly
                ? 'bg-brand-gold text-black intro-pulse'
                : 'text-brand-gold border border-brand-gold/30 hover:bg-brand-gold/10'
            }`}
            data-testid="filter-with-intro"
            aria-pressed={introOnly}
            title="Show only members who recorded a video intro"
          >
            <Play className={`w-3 h-3 ${introOnly ? 'fill-current' : ''}`} />
            With intro
          </button>

          <form onSubmit={handleSearch} className="ml-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, skill, city…"
              className="pl-10 bg-white/5 border-white/10 rounded-full text-sm text-white w-64 h-9"
              data-testid="search-input"
            />
          </form>
        </div>

        {/* Members Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="rounded-2xl bg-brand-surface border border-white/5 p-5 animate-pulse">
                <div className="w-14 h-14 bg-white/5 rounded-full mb-3" />
                <div className="h-4 bg-white/5 rounded w-3/4 mb-2" />
                <div className="h-3 bg-white/5 rounded w-1/2 mb-4" />
                <div className="h-8 bg-white/5 rounded" />
              </div>
            ))}
          </div>
        ) : members.length === 0 ? (
          <div className="bg-brand-surface border border-white/5 rounded-2xl p-12 text-center">
            <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No Members Found</h3>
            <p className="text-gray-400">Try adjusting your search or filter.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {members.map((member) => (
              <MemberCard key={member.id} member={member} onOpenIntro={setIntroMember} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-8">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-white/10 text-white" data-testid="prev-page-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="border-white/10 text-white" data-testid="next-page-btn">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>

      {introMember && (
        <IntroModal member={introMember} onClose={() => setIntroMember(null)} />
      )}
    </AppShell>
  );
}
